/* eslint-disable @angular-eslint/no-output-native */
import { KeyValuePipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  Renderer2,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { ControlValueAccessor } from '@angular/forms';
import { FormsModule, NgControl } from '@angular/forms';

import { Maybe } from '../../types/base/';
import { uniqueId } from '../../utils/operators';
import { ButtonComponent } from '../button';
import { IconComponent } from '../icon/icon.component';
import { EvcFormElementType, EvcInputSize, EvcInputType } from './input.type';
import { OptionsGroupComponent } from './options-group/options-group.component';

export type InputValueTypes = string | number | boolean | null;

@Component({
  selector: 'evc-input',
  templateUrl: 'input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [FormsModule, KeyValuePipe, IconComponent, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements ControlValueAccessor, OnInit {
  type = input<EvcInputType>('text');
  size = input<EvcInputSize>('md');
  label = input<string>();
  value = input<InputValueTypes>('');
  id = input<string>();
  name = input<InputValueTypes>('');
  autocomplete = input<string>('on');
  placeholder = input<string>();
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  readOnly = input<boolean>(false);
  clearable = input<boolean>(false);
  showError = input<boolean>(true);
  errorText = input<string>('');
  /** input text only */
  maxLength = input<number | null>(null);
  /** textarea only */
  rows = input<number | null>(null);
  /** checkbox only */
  checked = input<Maybe<boolean>>();
  indeterminate = input<boolean>(false);

  /** @output focus event */
  readonly focus = output<FocusEvent>();
  /** @output blur event */
  readonly blur = output<FocusEvent>();
  /** @output new value when change */
  readonly change = output<InputValueTypes>();

  input = viewChild<ElementRef<HTMLInputElement | HTMLTextAreaElement>>('input');
  inputGroupDiv = viewChild<ElementRef<HTMLInputElement>>('inputGroupDiv');

  /** direct access the input (or textarea) element */
  get nativeElement(): Maybe<HTMLInputElement | HTMLTextAreaElement> {
    return this.input()?.nativeElement;
  }

  localValue = signal<InputValueTypes>(this.value());
  showClearButton = computed(() => {
    return (
      this.clearable() &&
      this.localValue() !== '' &&
      this.localValue() !== null &&
      !this.isDisabled() &&
      !this.readOnly()
    );
  });
  protected formElementType: EvcFormElementType = 'input';

  safeId = computed(() => this.id() ?? uniqueId(String(this.name()) ?? 'input'));
  isDisabled = computed(() => this.controlDir?.disabled ?? this.disabled());

  #onChange: (value: InputValueTypes) => void = () => undefined;
  #onTouched: () => void = () => undefined;

  readonly injector = inject(Injector);
  readonly render = inject(Renderer2);
  readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly optionsGroup = inject(OptionsGroupComponent, { optional: true, host: true });
  readonly controlDir = inject(NgControl, { optional: true, self: true });

  constructor() {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }

    const effects = [
      effect(this.#warnIfIncorrectDisabledUsageEffect.bind(this)),
      effect(this.#syncValueEffect.bind(this)),
    ];

    this.#destroyRef.onDestroy(() => {
      effects.forEach((effectToClean) => effectToClean.destroy());
    });
  }

  ngOnInit(): void {
    const elementType = this.type();
    switch (elementType) {
      case 'textarea':
      case 'checkbox':
      case 'radio':
        this.formElementType = elementType;
        break;
      case 'switch':
        this.formElementType = 'checkbox';
        break;
      default:
        this.formElementType = 'input';
        break;
    }

    this.controlDir?.statusChanges?.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
  }

  /** clear the field and propagate the change */
  writeValue(value: InputValueTypes): void {
    this.localValue.set(value);
    const el = this.nativeElement;
    if (el) el.value = value?.toString() ?? '';

    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: InputValueTypes) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  onInputChanged(value: InputValueTypes, event?: Event): void {
    event?.stopPropagation();
    this.localValue.set(value);
    this.optionsGroup?.toggleValue(this.value());

    this.#onChange(value);
    this.change.emit(value);
  }

  onClear(): void {
    this.onInputChanged('');
  }

  onFocus(event: FocusEvent): void {
    this.focus.emit(event);
  }

  onBlur(event: FocusEvent): void {
    this.#onTouched();
    this.blur.emit(event);
  }

  hasErrors(): boolean | null {
    const isInvalid = this.controlDir?.invalid;

    return (this.showError() && isInvalid) ?? false;
  }

  #syncValueEffect(): void {
    const value = this.value();
    untracked(() => {
      if (value && value !== this.localValue()) {
        this.localValue.set(value);
      }
    });
  }

  #warnIfIncorrectDisabledUsageEffect(): void {
    const cannotUseDisabled = this.disabled() && this.controlDir;
    untracked(() => {
      if (cannotUseDisabled) {
        console.warn('use formControl.get(thisField).disable()');
      }
    });
  }
}
